/**
 * Mindtana brand behaviours (all progressive: the page works without this file).
 *
 *  1. Forest dividers: `mt-forest-divide` on the second of two touching deep forest sections.
 *  2. Header: `mt-header--scrolled` after 80px of scroll.
 *  3. Chart: `[data-mt-chart]` lines draw progressively as the chart moves from the bottom to
 *     the middle of the viewport (`--mt-chart-progress`), then stay drawn.
 *  4. Count-ups: `[data-mt-count]` numbers count from zero once in view, landing on the markup's
 *     final value.
 *  5. Leaf-to-tin: `[data-mt-steps]` crossfades the sticky image to the step in view.
 *  6. Live price: `[data-mt-live-price]` crossfades to the selected tier's price and line;
 *     `[data-mt-price-mirror]` elements (local nav, bottom sheet) follow it.
 *  7. Local nav: `[data-mt-localnav]` slides in beneath the header when the buy box leaves the
 *     viewport (desktop), with smooth-scrolling anchors and an active-section underline.
 *  8. Bottom sheet: on mobile the sticky bar's button opens `[data-mt-sheet]`, which borrows the
 *     plan picker and live price so the selection stays in sync with the buy box. The homepage
 *     has its own bar (`[data-mt-stickybar]`, shown once the hero has scrolled away) and a
 *     sheet with its own tiers whose button links to the product page with that plan selected.
 *     The header's menu icon opens the Mindtana menu drawer (`[data-mt-menu]`), a full-screen
 *     overlay that replaces Horizon's drawer.
 *  9. Gallery viewer: clicking a product image opens a full-screen viewer with arrows, keyboard,
 *     counter, click-to-zoom (desktop) and pinch-to-zoom (touch). On mobile the carousel dots
 *     are replaced by a progress bar.
 * 10. Accordions: `<details>` height animates open and closed (250ms ease-out).
 * 11. Image fade: images marked `data-mt-fade` fade in once loaded.
 * 12. Review tag chips: `[data-mt-review-filter]` chips filter the visible reviews.
 *
 * Reduced motion disables every animation and shows final states immediately.
 */
(function () {
  const reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)');
  const mobileQuery = window.matchMedia('(max-width: 749px)');
  const HEADER_SCROLLED_CLASS = 'mt-header--scrolled';
  const HEADER_SCROLL_OFFSET = 80;
  const COUNT_DURATION = 1200;

  document.documentElement.classList.add('mt-js');

  const prefersReduced = () => reducedMotion.matches;

  /* ---------------------------------------------------------------- dividers */
  function forestColor() {
    const probe = document.createElement('span');
    probe.style.cssText = 'position:absolute;visibility:hidden;background:var(--mt-forest,#22392E)';
    document.body.appendChild(probe);
    const color = getComputedStyle(probe).backgroundColor;
    probe.remove();
    return color;
  }

  function sectionBackground(section) {
    const background = section.querySelector(':scope > .section-background, :scope > * > .section-background');
    const candidates = [background, section, section.firstElementChild];
    for (const el of candidates) {
      if (!el) continue;
      const color = getComputedStyle(el).backgroundColor;
      if (color && color !== 'rgba(0, 0, 0, 0)' && color !== 'transparent') return color;
    }
    return null;
  }

  function markForestDividers() {
    const forest = forestColor();
    const sections = Array.from(document.querySelectorAll('.shopify-section')).filter((section) => {
      const style = getComputedStyle(section);
      return style.display !== 'none' && style.position !== 'fixed' && section.getBoundingClientRect().height > 0;
    });
    let previousIsForest = false;
    sections.forEach((section) => {
      const isForest = sectionBackground(section) === forest;
      section.classList.toggle('mt-forest-divide', isForest && previousIsForest);
      previousIsForest = isForest;
    });
  }

  let dividerTimer = null;
  function scheduleDividers() {
    clearTimeout(dividerTimer);
    dividerTimer = setTimeout(markForestDividers, 0);
  }

  /* --------------------------------------------------------------- scrolling */
  // Horizon scrolls `.page-wrapper` (not the window) from 990px up, so every scroll read,
  // scroll write and scroll listener goes through these helpers.
  function scroller() {
    const wrapper = document.querySelector('.page-wrapper');
    if (!wrapper) return null;
    const overflow = getComputedStyle(wrapper).overflowY;
    return overflow === 'auto' || overflow === 'scroll' ? wrapper : null;
  }

  function scrollTop() {
    const el = scroller();
    return el ? el.scrollTop : window.scrollY;
  }

  function scrollToY(top, behavior) {
    (scroller() || window).scrollTo({ top, behavior });
  }

  function onScroll(handler) {
    // Capture phase catches scroll events from any scroll container as well as the window.
    document.addEventListener('scroll', handler, { passive: true, capture: true });
  }

  /* ------------------------------------------------------------------ header */
  function headerHeight() {
    const header = document.getElementById('header-component');
    return header ? header.getBoundingClientRect().height : 0;
  }

  function syncHeader() {
    const header = document.getElementById('header-component');
    if (!header) return;
    header.classList.toggle(HEADER_SCROLLED_CLASS, scrollTop() > HEADER_SCROLL_OFFSET);
  }

  /* ------------------------------------------------------------- once in view */
  function onceInView(elements, callback, threshold) {
    if (!elements.length) return;
    if (!('IntersectionObserver' in window)) {
      elements.forEach(callback);
      return;
    }
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (!entry.isIntersecting) return;
          observer.unobserve(entry.target);
          callback(entry.target);
        });
      },
      { threshold: threshold || 0.35 }
    );
    elements.forEach((el) => observer.observe(el));
  }

  /* ------------------------------------------------------------------- chart */
  const charts = [];
  function registerCharts() {
    document.querySelectorAll('[data-mt-chart]:not(.is-wired)').forEach((chart) => {
      chart.classList.add('is-wired');
      charts.push({ el: chart, done: false });
    });
    syncCharts();
  }

  function syncCharts() {
    if (!charts.length) return;
    const vh = window.innerHeight;
    charts.forEach((chart) => {
      if (chart.done) return;
      if (prefersReduced()) {
        chart.el.style.setProperty('--mt-chart-progress', '1');
        chart.el.classList.add('is-drawn');
        chart.done = true;
        return;
      }
      const rect = chart.el.getBoundingClientRect();
      // 0 when the chart's top reaches the bottom of the viewport, 1 when it reaches the middle
      const progress = Math.min(Math.max((vh - rect.top) / (vh / 2), 0), 1);
      chart.el.style.setProperty('--mt-chart-progress', progress.toFixed(3));
      if (progress >= 1) {
        chart.el.classList.add('is-drawn');
        chart.done = true;
      }
    });
  }

  /* --------------------------------------------------------------- count-ups */
  function formatNumber(value, decimals) {
    return value.toLocaleString(undefined, { minimumFractionDigits: decimals, maximumFractionDigits: decimals });
  }

  function runCount(el) {
    if (el.classList.contains('is-counted')) return;
    el.classList.add('is-counted');
    const target = parseFloat(el.dataset.mtCount);
    const suffix = el.dataset.mtSuffix || '';
    const finalText = el.textContent.trim();
    if (Number.isNaN(target) || prefersReduced() || document.hidden) return;
    const decimals = (String(el.dataset.mtCount).split('.')[1] || '').length;
    const start = performance.now();
    const easeOut = (t) => 1 - Math.pow(1 - t, 3);
    let finished = false;
    const finish = () => {
      if (finished) return;
      finished = true;
      el.textContent = finalText;
    };
    function frame(now) {
      if (finished) return;
      const progress = Math.min((now - start) / COUNT_DURATION, 1);
      if (progress >= 1) return finish();
      el.textContent = formatNumber(target * easeOut(progress), decimals) + suffix;
      requestAnimationFrame(frame);
    }
    el.textContent = formatNumber(0, decimals) + suffix;
    requestAnimationFrame(frame);
    setTimeout(finish, COUNT_DURATION + 150);
  }

  function countUps() {
    onceInView(Array.from(document.querySelectorAll('[data-mt-count]:not(.is-counted)')), runCount, 0.6);
  }

  /* ------------------------------------------------------------- leaf to tin */
  function stepSections() {
    document.querySelectorAll('[data-mt-steps]:not(.is-wired)').forEach((section) => {
      section.classList.add('is-wired');
      const steps = Array.from(section.querySelectorAll('[data-mt-step]'));
      const frames = Array.from(section.querySelectorAll('[data-mt-frame]'));
      if (!steps.length || !frames.length) return;
      const activate = (index) => {
        frames.forEach((frame, i) => frame.classList.toggle('is-active', i === index));
        steps.forEach((step, i) => step.classList.toggle('is-active', i === index));
      };
      activate(0);
      if (!('IntersectionObserver' in window)) return;
      const observer = new IntersectionObserver(
        (entries) => entries.forEach((entry) => entry.isIntersecting && activate(Number(entry.target.dataset.mtStep))),
        { rootMargin: '-45% 0px -45% 0px', threshold: 0 }
      );
      steps.forEach((step) => observer.observe(step));
    });
  }

  /* -------------------------------------------------------------- live price */
  // One text node per element, so two values can never be on screen together: the old value
  // fades fully out, the text is swapped while invisible, then the new value fades in. Each
  // element keeps one state, so however fast the selection changes there is only ever one
  // animation running and it always ends on the latest value.
  const fades = new WeakMap();

  function crossfadeText(el, text, instant) {
    if (!el) return;
    let state = fades.get(el);
    if (!state) {
      state = { target: el.textContent, phase: 'idle', anim: null };
      fades.set(el, state);
    }
    if (state.target === text && (state.phase !== 'idle' || el.textContent === text)) return;
    state.target = text;

    const settle = () => {
      if (state.anim) state.anim.cancel();
      state.anim = null;
      state.phase = 'idle';
      el.textContent = state.target;
    };

    if (instant || prefersReduced() || document.hidden || !el.animate) return settle();

    // Safety net: if the animations are stalled (a throttled or background tab), land on the
    // latest value anyway shortly after the last change
    clearTimeout(state.timer);
    state.timer = setTimeout(() => {
      if (state.phase !== 'idle' || el.textContent !== state.target) settle();
    }, 700);

    if (state.phase === 'out') return; // the fade-out in flight will pick up the new target

    const fadeIn = () => {
      const previous = state.anim;
      el.textContent = state.target;
      state.phase = 'in';
      state.anim = el.animate([{ opacity: 0, transform: 'translateY(4px)' }, { opacity: 1, transform: 'translateY(0)' }], {
        duration: 200,
        easing: 'ease-out',
        fill: 'both',
      });
      if (previous) previous.cancel();
      const current = state.anim;
      current.onfinish = () => {
        if (state.anim !== current) return;
        current.cancel();
        state.anim = null;
        state.phase = 'idle';
        if (el.textContent !== state.target) crossfadeText(el, state.target);
      };
    };

    // From idle, or from part-way through a fade-in: fade out from the current opacity
    const from = state.anim ? parseFloat(getComputedStyle(el).opacity) || 0 : 1;
    const previous = state.anim;
    state.phase = 'out';
    state.anim = el.animate([{ opacity: from, transform: 'translateY(0)' }, { opacity: 0, transform: 'translateY(-4px)' }], {
      duration: Math.max(60, 200 * from),
      easing: 'ease-out',
      fill: 'both',
    });
    if (previous) previous.cancel();
    const outgoing = state.anim;
    outgoing.onfinish = () => {
      if (state.anim === outgoing) fadeIn();
    };
  }

  function selectedPlanInput(formId) {
    const selector = formId
      ? 'input[name="selling_plan"][form="' + formId + '"]:checked'
      : 'input[name="selling_plan"]:checked';
    return document.querySelector(selector);
  }

  // `instant` (page load) sets the selected tier's price straight away; a change crossfades
  function syncLivePrices(instant) {
    document.querySelectorAll('[data-mt-live-price]').forEach((block) => {
      const checked = selectedPlanInput(block.dataset.mtForm);
      if (!checked) return;
      const price = checked.dataset.mtPrice || '';
      const note = checked.dataset.mtPriceNote || '';
      crossfadeText(block.querySelector('.mt-live-price__value'), price, instant);
      crossfadeText(block.querySelector('.mt-live-price__note'), note, instant);
    });
    document.querySelectorAll('[data-mt-price-mirror]').forEach((el) => {
      const checked = selectedPlanInput(el.dataset.mtForm);
      if (checked) crossfadeText(el, checked.dataset.mtPrice || '', instant);
    });
  }

  document.addEventListener('change', (event) => {
    if (event.target && event.target.name === 'selling_plan') syncLivePrices(false);
  });

  /* --------------------------------------------------------------- local nav */
  function productForm() {
    return document.querySelector('form[id^="BuyButtons-ProductForm-"]');
  }

  function localNav() {
    const nav = document.querySelector('[data-mt-localnav]:not(.is-wired)');
    if (!nav) return;
    nav.classList.add('is-wired');

    const buyBox = document.querySelector('.product-form-buttons') || document.querySelector('.product-details');
    const links = Array.from(nav.querySelectorAll('[data-mt-anchor]'));
    const cta = nav.querySelector('[data-mt-localnav-cta]');
    const mainButton = document.querySelector('.product-form-buttons .add-to-cart-button');

    nav.style.setProperty('--mt-localnav-top', headerHeight() + 'px');

    // Line the bar's contents up with the page container
    const syncEdges = () => {
      const ref = Array.from(document.querySelectorAll('main .mt-container, .mt-container')).find(
        (el) => el.getBoundingClientRect().width > 0
      );
      if (!ref) return;
      const rect = ref.getBoundingClientRect();
      const style = getComputedStyle(ref);
      const left = rect.left + parseFloat(style.paddingLeft);
      const width = rect.width - parseFloat(style.paddingLeft) - parseFloat(style.paddingRight);
      if (width <= 0) return;
      nav.style.setProperty('--mt-localnav-left', left + 'px');
      nav.style.setProperty('--mt-localnav-width', width + 'px');
    };
    syncEdges();
    window.addEventListener('load', syncEdges);
    window.addEventListener('resize', syncEdges);

    // Header + bar + 16px breathing room. The bar is 56px by design; fall back to that if
    // the stylesheet has not applied yet when this runs.
    const offset = () => headerHeight() + (nav.getBoundingClientRect().height || 56) + 16;
    const syncOffset = () => document.documentElement.style.setProperty('--mt-anchor-offset', offset() + 'px');
    syncOffset();
    window.addEventListener('load', syncOffset);

    // CTA submits the product form, mirroring the main button's state
    if (cta) {
      const form = productForm();
      if (form) cta.setAttribute('form', form.getAttribute('id'));
      const mirror = () => {
        if (!mainButton) return;
        cta.disabled = mainButton.disabled;
        const text = mainButton.querySelector('.add-to-cart-text__content, .add-to-cart-text') || mainButton;
        if (text && text.textContent.trim()) cta.querySelector('[data-mt-localnav-label]').textContent = text.textContent.trim();
      };
      mirror();
      if (mainButton && 'MutationObserver' in window) {
        new MutationObserver(mirror).observe(mainButton, { attributes: true, childList: true, subtree: true, characterData: true });
      }
    }

    // Show when the buy box has scrolled out of the top of the viewport (desktop only)
    const show = (on) => nav.classList.toggle('is-visible', on && !mobileQuery.matches);
    const syncVisible = () => {
      if (buyBox) show(buyBox.getBoundingClientRect().bottom <= 0);
    };
    if (buyBox && 'IntersectionObserver' in window) {
      const observer = new IntersectionObserver(
        (entries) => entries.forEach((entry) => show(!entry.isIntersecting && entry.boundingClientRect.top < 0)),
        { threshold: 0 }
      );
      observer.observe(buyBox);
    }
    onScroll(syncVisible);
    syncVisible();
    window.addEventListener('resize', () => {
      nav.style.setProperty('--mt-localnav-top', headerHeight() + 'px');
      syncOffset();
      if (mobileQuery.matches) nav.classList.remove('is-visible');
    });
    onScroll(() => nav.style.setProperty('--mt-localnav-top', headerHeight() + 'px'));

    // Smooth scroll with offset
    links.forEach((link) => {
      link.addEventListener('click', (event) => {
        const target = document.querySelector(link.getAttribute('href'));
        if (!target) return;
        event.preventDefault();
        // Sections carry their own top padding; skip it so the first line lands just below the bar
        const pad = target.classList.contains('spacing-style') ? parseFloat(getComputedStyle(target).paddingTop) || 0 : 0;
        const top = target.getBoundingClientRect().top + pad + scrollTop() - offset();
        scrollToY(top, prefersReduced() ? 'auto' : 'smooth');
        history.replaceState(null, '', link.getAttribute('href'));
      });
    });

    // Active link: the last target whose top is above the bar
    const targets = links.map((link) => ({ link, el: document.querySelector(link.getAttribute('href')) })).filter((t) => t.el);
    const syncActive = () => {
      const line = offset() + 8;
      let active = null;
      targets.forEach((t) => {
        if (t.el.getBoundingClientRect().top <= line) active = t;
      });
      targets.forEach((t) => t.link.classList.toggle('is-active', t === active));
    };
    onScroll(syncActive);
    syncActive();
  }

  /* ------------------------------------------------------------ bottom sheet */
  function bottomSheet() {
    const sheet = document.querySelector('[data-mt-sheet]:not(.is-wired)');
    if (!sheet) return;
    sheet.classList.add('is-wired');

    // A sheet with its own content (homepage) lives on <body> so no section can clip it
    if (sheet.hasAttribute('data-mt-sheet-static')) {
      document.querySelectorAll('body > [data-mt-sheet-static].is-portaled').forEach((old) => old.remove());
      sheet.classList.add('is-portaled');
      document.body.appendChild(sheet);
    }

    const panel = sheet.querySelector('.mt-sheet__panel');
    const slot = sheet.querySelector('[data-mt-sheet-slot]');
    const overlay = sheet.querySelector('.mt-sheet__overlay');
    const closeButtons = sheet.querySelectorAll('[data-mt-sheet-close]');
    const cta = sheet.querySelector('[data-mt-sheet-cta]');
    const form = productForm();
    if (cta && form) cta.setAttribute('form', form.getAttribute('id'));
    const mainButton = document.querySelector('.product-form-buttons .add-to-cart-button');
    if (cta && mainButton) {
      const text = mainButton.querySelector('.add-to-cart-text__content, .add-to-cart-text') || mainButton;
      if (text.textContent.trim()) cta.querySelector('[data-mt-sheet-label]').textContent = text.textContent.trim();
      cta.disabled = mainButton.disabled;
    }

    const borrowed = []; // [{ el, placeholder }]
    const borrow = () => {
      ['[data-mt-live-price]', '.mt-plans'].forEach((selector) => {
        const el = document.querySelector('.product-details ' + selector);
        if (!el) return;
        const placeholder = document.createComment('mt-sheet-placeholder');
        el.parentNode.insertBefore(placeholder, el);
        slot.appendChild(el);
        borrowed.push({ el, placeholder });
      });
    };
    const giveBack = () => {
      borrowed.forEach(({ el, placeholder }) => {
        placeholder.parentNode.insertBefore(el, placeholder);
        placeholder.remove();
      });
      borrowed.length = 0;
    };

    let lastFocus = null;
    const open = () => {
      if (sheet.classList.contains('is-open')) return;
      lastFocus = document.activeElement;
      borrow();
      sheet.hidden = false;
      void sheet.offsetWidth; // flush so the slide-up transition runs
      sheet.classList.add('is-open');
      document.body.classList.add('mt-sheet-open');
      const first = sheet.querySelector('input:checked, button, [href]');
      if (first) first.focus({ preventScroll: true });
    };
    const close = () => {
      if (!sheet.classList.contains('is-open')) return;
      sheet.classList.remove('is-open');
      document.body.classList.remove('mt-sheet-open');
      const finish = () => {
        sheet.hidden = true;
        giveBack();
        if (lastFocus && lastFocus !== document.body && lastFocus.focus) lastFocus.focus({ preventScroll: true });
        else if (document.activeElement && document.activeElement.blur) document.activeElement.blur();
      };
      if (prefersReduced()) finish();
      else setTimeout(finish, 260);
    };

    // The mobile sticky bar's button opens the sheet instead of adding or scrolling: Horizon's
    // bar on the product page, the Mindtana bar on the homepage
    document.addEventListener(
      'click',
      (event) => {
        if (!sheet.isConnected) return;
        const button = event.target.closest('.sticky-add-to-cart__button, [data-mt-sheet-open]');
        if (!button || !mobileQuery.matches) return;
        event.preventDefault();
        event.stopPropagation();
        open();
      },
      true
    );

    // A sheet with its own tiers (homepage): its button is a link that follows the selection
    const link = sheet.querySelector('[data-mt-sheet-link]');
    if (link) {
      const syncLink = () => {
        const checked = sheet.querySelector('input[name="selling_plan"]:checked');
        if (!checked) return;
        if (checked.dataset.mtHref) {
          link.setAttribute('href', checked.dataset.mtHref);
          link.removeAttribute('aria-disabled');
        }
        if (checked.dataset.mtCta) link.textContent = checked.dataset.mtCta;
      };
      sheet.addEventListener('change', syncLink);
      syncLink();
    }

    overlay.addEventListener('click', close);
    closeButtons.forEach((button) => button.addEventListener('click', close));
    document.addEventListener('keydown', (event) => {
      if (event.key === 'Escape' && sheet.classList.contains('is-open')) close();
    });

    // Swipe down on the panel (from the handle or when the sheet content is scrolled to the top)
    let startY = null;
    let startScroll = 0;
    panel.addEventListener(
      'touchstart',
      (event) => {
        startY = event.touches[0].clientY;
        startScroll = panel.scrollTop;
      },
      { passive: true }
    );
    panel.addEventListener(
      'touchmove',
      (event) => {
        if (startY === null || startScroll > 0) return;
        const dy = event.touches[0].clientY - startY;
        if (dy > 0) panel.style.transform = 'translateY(' + dy + 'px)';
      },
      { passive: true }
    );
    panel.addEventListener('touchend', (event) => {
      if (startY === null) return;
      const dy = event.changedTouches[0].clientY - startY;
      panel.style.transform = '';
      startY = null;
      if (dy > 80 && startScroll === 0) close();
    });

    mobileQuery.addEventListener('change', () => {
      if (!mobileQuery.matches) close();
    });
  }

  /* ------------------------------------------------------------- menu drawer */
  // The header's menu icon opens the Mindtana drawer (`[data-mt-menu]`) instead of Horizon's.
  // The drawer's top row is laid over the live header so its close icon sits exactly where the
  // menu icon is. Closes on the icon, on Escape and when any link is tapped.
  let menuState = null;

  function menuDrawer() {
    const menu = document.querySelector('[data-mt-menu]:not(.is-wired)');
    if (!menu) return;
    menu.classList.add('is-wired');
    document.querySelectorAll('body > [data-mt-menu].is-portaled').forEach((old) => old.remove());
    menu.classList.add('is-portaled');
    document.body.appendChild(menu);

    const closeButton = menu.querySelector('[data-mt-menu-close]');
    let lastFocus = null;
    let hideTimer = null;

    // Copy the live header's geometry: its offset and height, the menu icon's centre, the logo's height
    const place = (trigger) => {
      const header = document.getElementById('header-component');
      if (!header) return;
      const bar = header.getBoundingClientRect();
      const top = Math.max(0, Math.round(bar.top));
      menu.style.setProperty('--mt-menu-bar-top', top + 'px');
      menu.style.setProperty('--mt-menu-bar-h', Math.round(bar.height) + 'px');
      const icon = trigger && (trigger.querySelector('.header-drawer-icon--open svg') || trigger.querySelector('svg') || trigger);
      if (icon) {
        const rect = icon.getBoundingClientRect();
        if (rect.width > 0) {
          menu.style.setProperty('--mt-menu-icon-x', rect.left + rect.width / 2 + 'px');
          menu.style.setProperty('--mt-menu-icon-y', rect.top + rect.height / 2 - top + 'px');
        }
      }
      const logo = header.querySelector('.header-logo__image, .header-logo img');
      if (logo && logo.getBoundingClientRect().height > 0) {
        const rect = logo.getBoundingClientRect();
        menu.style.setProperty('--mt-menu-logo-h', rect.height + 'px');
        menu.style.setProperty('--mt-menu-logo-y', rect.top + rect.height / 2 - top + 'px');
      }
    };

    const isOpen = () => menu.classList.contains('is-open');

    const open = (trigger) => {
      if (isOpen()) return;
      clearTimeout(hideTimer);
      place(trigger);
      lastFocus = trigger || document.activeElement;
      menu.hidden = false;
      void menu.offsetWidth; // flush so the slide runs
      menu.classList.add('is-open');
      document.body.classList.add('mt-menu-open');
      if (trigger) trigger.setAttribute('aria-expanded', 'true');
      if (closeButton) closeButton.focus({ preventScroll: true });
    };

    const close = () => {
      if (!isOpen()) return;
      menu.classList.remove('is-open');
      document.body.classList.remove('mt-menu-open');
      if (lastFocus && lastFocus.setAttribute) lastFocus.setAttribute('aria-expanded', 'false');
      const finish = () => {
        menu.hidden = true;
        if (lastFocus && lastFocus !== document.body && lastFocus.focus) lastFocus.focus({ preventScroll: true });
      };
      if (prefersReduced()) finish();
      else hideTimer = setTimeout(finish, 260);
    };

    menu.addEventListener('click', (event) => {
      if (event.target.closest('[data-mt-menu-close]')) {
        close();
        return;
      }
      const link = event.target.closest('a[href]');
      if (!link) return;
      // A link to a section that is on this page ("/#faq"): close and scroll there instead of reloading
      if (link.hasAttribute('data-mt-menu-anchor')) {
        let url = null;
        try {
          url = new URL(link.getAttribute('href'), window.location.href);
        } catch (error) {
          url = null;
        }
        const target = url && url.hash ? document.getElementById(url.hash.slice(1)) : null;
        if (target) {
          event.preventDefault();
          close();
          setTimeout(() => target.scrollIntoView({ behavior: prefersReduced() ? 'auto' : 'smooth', block: 'start' }), 0);
          return;
        }
      }
      close();
    });

    menuState = { menu, open, close, isOpen };
  }

  // Listeners that must exist once, however often sections re-render
  window.addEventListener(
    'click',
    (event) => {
      if (!menuState || !menuState.menu.isConnected) return;
      const trigger = event.target.closest && event.target.closest('.header__icon--menu');
      if (!trigger) return;
      // Window capture runs before Horizon's own handler and before <details> toggles
      event.preventDefault();
      event.stopImmediatePropagation();
      menuState.open(trigger);
    },
    true
  );

  document.addEventListener('keydown', (event) => {
    if (!menuState || !menuState.isOpen()) return;
    if (event.key === 'Escape') {
      event.preventDefault();
      menuState.close();
      return;
    }
    if (event.key !== 'Tab') return;
    // Keep focus inside the drawer while it is open
    const focusable = Array.from(menuState.menu.querySelectorAll('a[href], button:not([disabled])')).filter(
      (el) => el.getBoundingClientRect().width > 0
    );
    if (!focusable.length) return;
    const first = focusable[0];
    const last = focusable[focusable.length - 1];
    if (event.shiftKey && document.activeElement === first) {
      event.preventDefault();
      last.focus();
    } else if (!event.shiftKey && document.activeElement === last) {
      event.preventDefault();
      first.focus();
    }
  });

  /* -------------------------------------------------------------- sticky bar */
  // Homepage: the bar shows once the hero has scrolled out of view (mobile only). On both
  // pages the bar's height is published as --mt-stickybar-h so the footer can keep clear of it.
  function stickyBar() {
    const publishHeight = (el) => {
      const height = el ? el.getBoundingClientRect().height : 0;
      if (height > 0) document.documentElement.style.setProperty('--mt-stickybar-h', Math.ceil(height) + 'px');
    };

    const horizonBar = document.querySelector('.sticky-add-to-cart__bar');
    if (horizonBar && !horizonBar.classList.contains('mt-measured')) {
      horizonBar.classList.add('mt-measured');
      const measure = () => publishHeight(horizonBar);
      measure();
      window.addEventListener('load', measure);
      window.addEventListener('resize', measure);
      if ('ResizeObserver' in window) new ResizeObserver(measure).observe(horizonBar);
    }

    const bar = document.querySelector('[data-mt-stickybar]:not(.is-wired)');
    if (!bar) return;
    bar.classList.add('is-wired');
    document.querySelectorAll('body > [data-mt-stickybar].is-portaled').forEach((old) => old.remove());
    bar.classList.add('is-portaled');
    document.body.appendChild(bar);

    const hero = document.querySelector('.mt-hero');
    const sync = () => {
      if (!bar.isConnected) return;
      const past = hero ? hero.getBoundingClientRect().bottom <= 0 : scrollTop() > window.innerHeight * 0.6;
      bar.classList.toggle('is-visible', past && mobileQuery.matches);
      publishHeight(bar);
    };
    onScroll(sync);
    window.addEventListener('resize', sync);
    window.addEventListener('load', sync);
    sync();
  }

  /* ---------------------------------------------------------- gallery viewer */
  function largeSrc(img) {
    const src = img.currentSrc || img.src;
    if (/[?&]width=\d+/.test(src)) return src.replace(/([?&])width=\d+/, '$1width=2000');
    return src;
  }

  function galleryViewer() {
    const media = document.querySelector('.product-information__media');
    if (!media || media.classList.contains('mt-viewer-wired')) return;
    media.classList.add('mt-viewer-wired');
    // Horizon renders the grid and the mobile carousel from the same media, so dedupe by
    // source (ignoring the width parameter) and map every rendered image to its unique index.
    const sourceKey = (img) => (img.currentSrc || img.src || '').replace(/([?&])width=\d+&?/, '$1').replace(/[?&]$/, '');
    const rendered = Array.from(media.querySelectorAll('.product-media__image'));
    const images = [];
    const indexOf = new Map();
    rendered.forEach((img) => {
      const key = sourceKey(img);
      if (!indexOf.has(key)) {
        indexOf.set(key, images.length);
        images.push(img);
      }
    });
    if (!images.length) return;

    // Build the viewer once
    const viewer = document.createElement('div');
    viewer.className = 'mt-viewer';
    viewer.hidden = true;
    viewer.setAttribute('role', 'dialog');
    viewer.setAttribute('aria-modal', 'true');
    viewer.setAttribute('aria-label', 'Product images');
    viewer.innerHTML =
      '<button type="button" class="mt-viewer__close" data-mt-viewer-close aria-label="Close">' +
      '<svg viewBox="0 0 16 16" width="16" height="16" aria-hidden="true"><path d="M2 2l12 12M14 2 2 14" stroke="currentColor" stroke-width="1.5" fill="none"/></svg></button>' +
      '<button type="button" class="mt-viewer__arrow mt-viewer__arrow--prev" data-mt-viewer-prev aria-label="Previous image">' +
      '<svg viewBox="0 0 16 16" width="16" height="16" aria-hidden="true"><path d="M10 2 4 8l6 6" stroke="currentColor" stroke-width="1.5" fill="none"/></svg></button>' +
      '<button type="button" class="mt-viewer__arrow mt-viewer__arrow--next" data-mt-viewer-next aria-label="Next image">' +
      '<svg viewBox="0 0 16 16" width="16" height="16" aria-hidden="true"><path d="m6 2 6 6-6 6" stroke="currentColor" stroke-width="1.5" fill="none"/></svg></button>' +
      '<div class="mt-viewer__stage"><img class="mt-viewer__image" alt=""></div>' +
      '<p class="mt-viewer__counter mt-label mt-label--muted" aria-live="polite"></p>';
    document.body.appendChild(viewer);

    const stage = viewer.querySelector('.mt-viewer__stage');
    const image = viewer.querySelector('.mt-viewer__image');
    const counter = viewer.querySelector('.mt-viewer__counter');
    let index = 0;
    let zoomed = false;
    let scale = 1;
    let lastFocus = null;

    const render = () => {
      const source = images[index];
      image.src = largeSrc(source);
      image.alt = source.alt || '';
      counter.textContent = index + 1 + ' / ' + images.length;
      resetZoom();
    };
    const resetZoom = () => {
      zoomed = false;
      scale = 1;
      image.style.transform = '';
      image.style.transformOrigin = '';
      viewer.classList.remove('is-zoomed');
    };
    const go = (delta) => {
      index = (index + delta + images.length) % images.length;
      render();
    };
    const open = (i) => {
      index = i;
      lastFocus = document.activeElement;
      viewer.hidden = false;
      void viewer.offsetWidth; // flush so the opacity transition runs
      viewer.classList.add('is-open');
      document.body.classList.add('mt-viewer-open');
      render();
      viewer.querySelector('[data-mt-viewer-close]').focus({ preventScroll: true });
    };
    const close = () => {
      viewer.classList.remove('is-open');
      document.body.classList.remove('mt-viewer-open');
      const finish = () => {
        viewer.hidden = true;
        resetZoom();
        if (lastFocus && lastFocus !== document.body && lastFocus.focus) lastFocus.focus({ preventScroll: true });
        else if (document.activeElement && document.activeElement.blur) document.activeElement.blur();
      };
      if (prefersReduced()) finish();
      else setTimeout(finish, 220);
    };

    rendered.forEach((img) => {
      const target = img.closest('a, button') || img;
      target.style.cursor = 'zoom-in';
      target.addEventListener('click', (event) => {
        event.preventDefault();
        event.stopPropagation();
        open(indexOf.get(sourceKey(img)) || 0);
      });
    });

    viewer.querySelector('[data-mt-viewer-close]').addEventListener('click', close);
    viewer.querySelector('[data-mt-viewer-prev]').addEventListener('click', () => go(-1));
    viewer.querySelector('[data-mt-viewer-next]').addEventListener('click', () => go(1));
    viewer.addEventListener('click', (event) => {
      if (event.target === viewer || event.target === stage) close();
    });
    document.addEventListener('keydown', (event) => {
      if (viewer.hidden) return;
      if (event.key === 'Escape') close();
      if (event.key === 'ArrowLeft') go(-1);
      if (event.key === 'ArrowRight') go(1);
    });

    // Desktop: click toggles a 2x zoom that follows the cursor
    const follow = (event) => {
      const rect = image.getBoundingClientRect();
      const x = ((event.clientX - rect.left) / rect.width) * 100;
      const y = ((event.clientY - rect.top) / rect.height) * 100;
      image.style.transformOrigin = x + '% ' + y + '%';
    };
    image.addEventListener('click', (event) => {
      if (event.pointerType === 'touch' || mobileQuery.matches) return;
      zoomed = !zoomed;
      viewer.classList.toggle('is-zoomed', zoomed);
      image.style.transform = zoomed ? 'scale(2)' : '';
      if (zoomed) follow(event);
      else image.style.transformOrigin = '';
    });
    image.addEventListener('mousemove', (event) => {
      if (zoomed) follow(event);
    });

    // Touch: pinch to zoom, swipe to move between images
    let pinchStart = null;
    let swipeStart = null;
    stage.addEventListener(
      'touchstart',
      (event) => {
        if (event.touches.length === 2) {
          const [a, b] = event.touches;
          pinchStart = { distance: Math.hypot(a.clientX - b.clientX, a.clientY - b.clientY), scale };
          const rect = image.getBoundingClientRect();
          const cx = ((a.clientX + b.clientX) / 2 - rect.left) / rect.width;
          const cy = ((a.clientY + b.clientY) / 2 - rect.top) / rect.height;
          image.style.transformOrigin = cx * 100 + '% ' + cy * 100 + '%';
        } else if (event.touches.length === 1 && scale === 1) {
          swipeStart = event.touches[0].clientX;
        }
      },
      { passive: true }
    );
    stage.addEventListener(
      'touchmove',
      (event) => {
        if (pinchStart && event.touches.length === 2) {
          const [a, b] = event.touches;
          const distance = Math.hypot(a.clientX - b.clientX, a.clientY - b.clientY);
          scale = Math.min(Math.max((distance / pinchStart.distance) * pinchStart.scale, 1), 4);
          image.style.transform = 'scale(' + scale + ')';
          viewer.classList.toggle('is-zoomed', scale > 1);
        }
      },
      { passive: true }
    );
    stage.addEventListener('touchend', (event) => {
      if (pinchStart && event.touches.length < 2) pinchStart = null;
      if (swipeStart !== null && event.changedTouches.length) {
        const dx = event.changedTouches[0].clientX - swipeStart;
        swipeStart = null;
        if (Math.abs(dx) > 50 && scale === 1) go(dx < 0 ? 1 : -1);
      }
    });

    // Mobile progress bar in place of the dots
    const controls = media.querySelector('.media-gallery__mobile-controls');
    const slideshow = media.querySelector('slideshow-component');
    if (controls && slideshow && images.length > 1) {
      const bar = document.createElement('div');
      bar.className = 'mt-gallery-progress';
      bar.setAttribute('aria-hidden', 'true');
      bar.innerHTML = '<span class="mt-gallery-progress__fill"></span>';
      controls.parentNode.insertBefore(bar, controls);
      const fill = bar.querySelector('.mt-gallery-progress__fill');
      const slides = Array.from(slideshow.querySelectorAll('slideshow-slide'));
      const track = slideshow.querySelector('slideshow-slides');
      const setIndex = (i) => {
        const index = Math.min(Math.max(i, 0), slides.length - 1);
        fill.style.width = ((index + 1) / slides.length) * 100 + '%';
      };
      // Nearest slide to the track's scroll position, so the bar follows the swipe itself
      const fromScroll = () => {
        if (!track || !track.clientWidth) return false;
        const origin = track.getBoundingClientRect().left;
        let nearest = 0;
        let distance = Infinity;
        slides.forEach((slide, i) => {
          const d = Math.abs(slide.getBoundingClientRect().left - origin);
          if (d < distance) {
            distance = d;
            nearest = i;
          }
        });
        setIndex(nearest);
        return true;
      };
      const update = (event) => {
        if (event && event.detail && typeof event.detail.index === 'number') return setIndex(event.detail.index);
        if (fromScroll()) return;
        setIndex(slides.findIndex((s) => s.getAttribute('aria-hidden') === 'false'));
      };
      update();
      slideshow.addEventListener('slideshow:select', update);
      if (track) track.addEventListener('scroll', fromScroll, { passive: true });
    }
  }

  /* -------------------------------------------------------------- accordions */
  function accordions() {
    document.querySelectorAll('details:not(.mt-anim)').forEach((details) => {
      const summary = details.querySelector(':scope > summary');
      const content = summary && summary.nextElementSibling;
      if (!summary || !content) return;
      details.classList.add('mt-anim');
      let animation = null;

      summary.addEventListener('click', (event) => {
        if (prefersReduced() || !content.animate) return; // native toggle
        event.preventDefault();
        if (animation) animation.cancel();
        if (details.open) {
          const start = content.offsetHeight;
          details.style.overflow = 'hidden';
          animation = content.animate([{ height: start + 'px', opacity: 1 }, { height: '0px', opacity: 0 }], {
            duration: 250,
            easing: 'ease-out',
          });
          details.classList.remove('is-open');
          animation.onfinish = () => {
            details.open = false;
            details.style.overflow = '';
            animation = null;
          };
        } else {
          details.open = true;
          details.classList.add('is-open');
          const end = content.offsetHeight;
          details.style.overflow = 'hidden';
          animation = content.animate([{ height: '0px', opacity: 0 }, { height: end + 'px', opacity: 1 }], {
            duration: 250,
            easing: 'ease-out',
          });
          animation.onfinish = () => {
            details.style.overflow = '';
            animation = null;
          };
        }
      });
      details.classList.toggle('is-open', details.open);
    });
  }

  /* -------------------------------------------------------------- image fade */
  function imageFade() {
    document.querySelectorAll('img[data-mt-fade]:not(.is-loaded), .mt-image img:not(.is-loaded), .product-media__image:not(.is-loaded), .mt-hero__tin:not(.is-loaded), .mt-recipe__image img:not(.is-loaded), .mt-plan__thumb-img:not(.is-loaded)').forEach((img) => {
      const mark = () => img.classList.add('is-loaded');
      if (img.complete && img.naturalWidth > 0) mark();
      else {
        img.addEventListener('load', mark, { once: true });
        img.addEventListener('error', mark, { once: true });
      }
    });
  }

  /* ------------------------------------------------------------ review chips */
  function reviewFilters() {
    document.querySelectorAll('[data-mt-review-filter]:not(.is-wired)').forEach((row) => {
      row.classList.add('is-wired');
      const section = row.closest('.mt-reviews');
      const reviews = section ? Array.from(section.querySelectorAll('.mt-review')) : [];
      const chips = Array.from(row.querySelectorAll('[data-mt-tag]'));
      let active = null;
      const apply = () => {
        chips.forEach((chip) => chip.setAttribute('aria-pressed', chip.dataset.mtTag === active ? 'true' : 'false'));
        reviews.forEach((review) => {
          const tags = (review.dataset.mtTags || '').split('|').filter(Boolean);
          review.hidden = active ? !tags.includes(active) : false;
        });
      };
      chips.forEach((chip) =>
        chip.addEventListener('click', () => {
          active = active === chip.dataset.mtTag ? null : chip.dataset.mtTag;
          apply();
        })
      );
    });
  }

  /* -------------------------------------------------------------------- init */
  function init() {
    scheduleDividers();
    syncHeader();
    registerCharts();
    countUps();
    stepSections();
    syncLivePrices(true);
    localNav();
    bottomSheet();
    menuDrawer();
    stickyBar();
    galleryViewer();
    accordions();
    imageFade();
    reviewFilters();
  }

  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', init);
  else init();

  let ticking = false;
  onScroll(() => {
    syncHeader();
    if (ticking) return;
    ticking = true;
    requestAnimationFrame(() => {
      syncCharts();
      ticking = false;
    });
  });
  window.addEventListener('resize', syncCharts);
  window.addEventListener('load', () => {
    syncHeader();
    syncCharts();
  });

  ['shopify:section:load', 'shopify:section:unload', 'shopify:section:reorder', 'shopify:section:select'].forEach((name) =>
    document.addEventListener(name, init)
  );
})();
